import type { WorkflowConfig } from '@/types/workflows'
import type { FactoryConfig, FactoryScheduleRecord, NewFactorySchedulePayload } from '@/types/factories'
import type { ScheduledRun } from '@/types/schedules'
import type { Automation, AutoHelper, AutoInput, AutoStep, AutoWhen } from '@/types/automations'
import { utcTimesToLocal12Hour, local12HourToUtcTime, type Local12HourTime } from '@/utils/scheduleTime'
import type { UpdateScheduledRunPayload } from '@/types/schedules'
import { buildScheduledRunPayload } from '@/utils/schedules'
import { agentTitleForSelector, findAgentItemBySelector, type ResolvableAgentItem } from '@/utils/agentRefs'
import { buildWorkflowHelperRegex, denormalizeHelperAgentRefs, displayWorkflowDataKey } from '@/utils/workflowHelpers'
import { newId } from '@/utils/general'

// The simple-mode lens shows agents by their human name. Workflows/factories store agents as canonical
// refs (`agent/<id>`) or ids, so resolving needs the accessible-agent catalog — the same one the
// Advanced WorkflowEditor uses (store getter `accessibleAgentItems`). An empty catalog leaves the raw
// selector in place (graceful fallback), so existing callers without it still render.
type AgentCatalog = ResolvableAgentItem[]

// Read adapter: maps the real WorkflowConfig / FactoryConfig / ScheduledRun records into the
// plain-language "Automation" lens. This is the read direction only — persisting lens edits back
// into the underlying configs is a backend-coupled seam handled by the existing Advanced editors.
// See msqweb/DESIGN_PHILOSOPHY.md (§4 mapping, §5 engine constraints).

/** Extract `<agentSelector|#|dataKey>` helper tokens from a workflow's main prompt. */
export function parseWorkflowHelpers(mainPrompt: string, delimiter?: string | null): AutoHelper[] {
  if (!mainPrompt) return []
  const regex = buildWorkflowHelperRegex(delimiter)
  const helpers: AutoHelper[] = []
  const seen = new Set<string>()
  let match: RegExpExecArray | null
  while ((match = regex.exec(mainPrompt)) !== null) {
    const agent = match[1].trim()
    // The "to read" value is shown as a plain data name (a single `{{path}}` unwraps to `path`),
    // using the same rule the token rewriter applies — see displayWorkflowDataKey.
    const input = displayWorkflowDataKey(match[2])
    const key = `${agent}::${input}`
    if (seen.has(key)) continue
    seen.add(key)
    helpers.push({ agent, input, role: '' })
  }
  return helpers
}

// dataPayload is a JSON string. Its exact shape is owned by the Advanced payload editor, so we parse
// defensively and only surface a flat key/value view — never guess a richer structure.
export function inputsFromPayload(dataPayload: string): AutoInput[] {
  if (!dataPayload) return []
  try {
    const parsed = JSON.parse(dataPayload)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.entries(parsed as Record<string, unknown>).map(([key, value]) => ({
        key,
        value: typeof value === 'string' ? value : JSON.stringify(value)
      }))
    }
  } catch {
    // Non-JSON / free-text payloads surface no inputs rather than a fabricated shape.
  }
  return []
}

export function automationFromWorkflow(w: WorkflowConfig, agents: AgentCatalog = []): Automation {
  const helpers = parseWorkflowHelpers(w.mainPrompt, w.delimiter).map((h) => ({
    ...h,
    agent: agentTitleForSelector(h.agent, agents)
  }))
  return {
    id: w.id,
    type: 'workflow',
    name: w.name,
    status: 'active',
    // Plain-language goal shown to the user: resolve helper-token agent refs to names (the raw prompt
    // stores `agent/<id>` refs, even for new workflows — the backend normalises names to refs on save).
    // ownedOnly matches the Advanced editor's editing form: only owned refs become names, because only
    // owned names can be re-normalized to refs on save — shared selectors must stay canonical.
    job: denormalizeHelperAgentRefs(w.mainPrompt, w.delimiter, agents, { ownedOnly: true }),
    inputs: inputsFromPayload(w.dataPayload),
    dataPayload: w.dataPayload ?? '{}',
    concurrency: w.concurrency,
    helpers,
    // mainAgentRef is a canonical ref; mainAgentId is a bare id — resolve either to the agent's name.
    lead: agentTitleForSelector(w.mainAgentRef || w.mainAgentId || '', agents, { matchBareId: true }),
    // A workflow cannot be scheduled directly (it would be wrapped in a factory) — known seam.
    when: { mode: 'ondemand' }
  }
}

// Mirrors FactoryScheduleCard.getReferenceDateFor: the date UTC times are converted against.
function factoryScheduleReference(s: FactoryScheduleRecord): Date {
  if (s.repeatInterval === 'once') return new Date(s.startDate)
  return new Date(s.nextRunAt ?? s.startDate)
}

export function automationFromFactory(
  f: FactoryConfig,
  agents: AgentCatalog = [],
  workflows: WorkflowConfig[] = [],
  schedules: FactoryScheduleRecord[] = []
): Automation {
  const steps: AutoStep[] = (f.steps || [])
    .slice()
    .sort((a, b) => a.index - b.index)
    .map((s) => {
      let then: AutoStep['then'] = 'next'
      let loopTo: number | undefined
      if (s.transition.kind === 'stop') {
        then = 'stop'
      } else if (s.transition.kind === 'loop_to_index') {
        then = 'loop'
        loopTo = s.transition.targetIndex
      }
      // Workflow-kind steps run a workflow, not an agent — the lens carries the workflow's id (the
      // field the write-back persists) plus its name for display. Their agent stays empty even
      // when the backend config carries a stale agentRef.
      const isWorkflowStep = s.kind === 'workflow'
      return {
        stepId: s.stepId,
        name: s.name,
        agent: isWorkflowStep ? '' : agentTitleForSelector(s.agentRef?.agentRef || '', agents),
        kind: s.kind,
        workflowId: isWorkflowStep ? s.workflowRef?.workflowConfigId : undefined,
        workflowName: isWorkflowStep
          ? workflows.find((w) => w.id === s.workflowRef?.workflowConfigId)?.name || 'Workflow'
          : undefined,
        payloadSchema: isWorkflowStep ? s.workflowRef?.payloadSchema : undefined,
        // Fixer is stored as a canonical ref (or a legacy bare id) — show the agent's name.
        fixerAgent: isWorkflowStep
          ? agentTitleForSelector(s.workflowRef?.fixerAgentRef || s.workflowRef?.fixerAgentId || '', agents, { matchBareId: true })
          : undefined,
        maxRepairAttempts: isWorkflowStep ? s.workflowRef?.maxRepairAttempts ?? 0 : undefined,
        then,
        loopTo
      }
    })
  // Factory schedules are separate records; the simple lens manages the FIRST one (Advanced supports
  // several). An enabled/running schedule wins the display over `continuous` — the write-back keeps
  // the two exclusive (picking schedule clears continuous; picking another mode disables the record).
  const schedule = schedules[0]
  const scheduled = !!schedule && schedule.status !== 'disabled'
  const when: AutoWhen = scheduled
    ? { mode: 'schedule', ...scheduleWhenFields(schedule, factoryScheduleReference(schedule)) }
    : { mode: f.continuous ? 'continuous' : 'ondemand' }
  return {
    id: f.id,
    type: 'factory',
    name: f.name,
    status: 'active',
    job: f.description || f.name,
    inputs: [],
    // Mirrors the Advanced "Initial Payload" field default. Session-only; handed to execute() on Run.
    carryPayload: 'execute',
    steps,
    when
  }
}

const FREQ_LABEL: Record<ScheduledRun['repeatInterval'], string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  once: 'Once'
}

const DAY_LABEL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

// Reverse of FREQ_LABEL, for folding edits back into the underlying ScheduledRun.
const LABEL_FREQ: Record<string, ScheduledRun['repeatInterval']> = {
  Daily: 'daily',
  Weekly: 'weekly',
  Monthly: 'monthly',
  Once: 'once'
}

function formatLocal12Hour(t: Local12HourTime): string {
  return `${t.hour}:${String(t.minute).padStart(2, '0')} ${t.ampm}`
}

// Parse a "h:mm AM/PM" label (the WhenEditor preset set) back into a structured local time. Falls
// back to 9:00 AM, matching the Advanced editor's default, when the string is missing/unparseable.
function parseLocal12Hour(time?: string): Local12HourTime {
  const m = (time || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!m) return { hour: 9, minute: 0, ampm: 'AM' }
  return { hour: parseInt(m[1], 10), minute: parseInt(m[2], 10), ampm: m[3].toUpperCase() as 'AM' | 'PM' }
}

// Shared by the ScheduledRun and factory-schedule lenses: surface UTC schedule timing as the simple
// "when" fields. Only the first time/day is shown — the write-backs preserve any extras.
function scheduleWhenFields(
  r: {
    timesToRun?: Array<{ hour: number; minute: number }>
    repeatInterval: ScheduledRun['repeatInterval']
    daysOfWeek?: number[]
    dayOfMonth?: number
  },
  reference: Date
): Pick<AutoWhen, 'freq' | 'day' | 'dayOfMonth' | 'time'> {
  const local = utcTimesToLocal12Hour(r.timesToRun || [], reference)[0]
  return {
    freq: FREQ_LABEL[r.repeatInterval],
    day: r.daysOfWeek && r.daysOfWeek.length ? DAY_LABEL[r.daysOfWeek[0]] : undefined,
    dayOfMonth: r.dayOfMonth,
    time: local ? formatLocal12Hour(local) : '9:00 AM'
  }
}

export function automationFromScheduledRun(r: ScheduledRun): Automation {
  // Mirror ScheduledRunCard.updateData: convert UTC times to local using the run's startDate as the
  // reference date, so the displayed time matches the Advanced editor exactly.
  const reference = new Date(r.startDate)
  const when: AutoWhen = {
    mode: 'schedule',
    ...scheduleWhenFields(r, reference),
    // Advanced defaults a missing sendEmail to true (ScheduledRunCard.updateData).
    sendEmail: r.sendEmail === undefined ? true : r.sendEmail
  }
  return {
    id: r.id,
    type: 'schedule',
    name: r.label || r.agentName,
    status: r.status === 'disabled' ? 'paused' : 'active',
    job: r.prompt,
    inputs: [],
    lead: r.agentName,
    when
  }
}

// Write direction — fold simple-lens schedule edits back onto the real ScheduledRun. The final
// payload shape (daysOfWeek only for weekly, dayOfMonth only for monthly, status mapping) comes from
// buildScheduledRunPayload, the same builder the Advanced ScheduledRunCard saves through. The source
// run supplies every field the simple UI doesn't expose (startDate, agentName, any additional times,
// deliveryMethod, …) so nothing is lost.
export function scheduledRunPayloadFromAutomation(
  automation: Automation,
  source: ScheduledRun
): UpdateScheduledRunPayload {
  const when = automation.when
  const repeatInterval = LABEL_FREQ[when.freq || ''] ?? source.repeatInterval
  const reference = new Date(source.startDate)
  const utc = local12HourToUtcTime(parseLocal12Hour(when.time), reference)
  // Preserve any additional times the run already had beyond the single one the simple UI shows.
  const timesToRun =
    source.timesToRun && source.timesToRun.length > 1 ? [utc, ...source.timesToRun.slice(1)] : [utc]
  const dayIndex = Math.max(0, DAY_LABEL.indexOf(when.day || ''))
  // The simple UI shows a single weekday (daysOfWeek[0]); preserve any additional days the run had,
  // the same way extra times are kept above, so editing a multi-day schedule isn't lossy.
  const daysOfWeek =
    source.daysOfWeek && source.daysOfWeek.length > 1 ? [dayIndex, ...source.daysOfWeek.slice(1)] : [dayIndex]
  return buildScheduledRunPayload({
    // The schedule's "lead" is the agent that runs it (read as r.agentName); persist a change to it,
    // mirroring the Advanced editor's Agent select. Falls back to the source agent if unset.
    agentName: automation.lead || source.agentName,
    label: automation.name,
    prompt: automation.job,
    startDate: source.startDate,
    timesToRun,
    repeatInterval,
    daysOfWeek,
    dayOfMonth: when.dayOfMonth ?? source.dayOfMonth ?? 1,
    enabled: automation.status === 'active',
    sendEmail: when.sendEmail === undefined ? true : when.sendEmail
  })
}

// The "What it works with" inputs as the JSON-string carry payload factory runs start from (the same
// form the Advanced editor's manual payload field sends). undefined when no input has a key.
export function carryPayloadFromInputs(inputs: AutoInput[] | undefined): string | undefined {
  const filled = (inputs || []).filter((i) => i.key.trim())
  if (!filled.length) return undefined
  return JSON.stringify(Object.fromEntries(filled.map((i) => [i.key.trim(), i.value])))
}

// Write direction — the lens-managed (first) factory schedule record. Mirrors
// scheduledRunPayloadFromAutomation: the source record supplies everything the simple UI doesn't
// expose (extra times/days, label, carry payload, startDate); without a source a new record is
// built. Selecting schedule mode means "enabled" — except a 'running' status is preserved, matching
// the Advanced card, which blocks status changes mid-run.
export function factorySchedulePayloadFromAutomation(
  automation: Automation,
  source?: FactoryScheduleRecord
): NewFactorySchedulePayload | FactoryScheduleRecord {
  const when = automation.when
  const repeatInterval = LABEL_FREQ[when.freq || ''] ?? source?.repeatInterval ?? 'daily'
  const reference = source ? factoryScheduleReference(source) : new Date()
  const utc = local12HourToUtcTime(parseLocal12Hour(when.time), reference)
  const timesToRun =
    source?.timesToRun && source.timesToRun.length > 1 ? [utc, ...source.timesToRun.slice(1)] : [utc]
  const dayIndex = Math.max(0, DAY_LABEL.indexOf(when.day || ''))
  const daysOfWeek =
    source?.daysOfWeek && source.daysOfWeek.length > 1 ? [dayIndex, ...source.daysOfWeek.slice(1)] : [dayIndex]
  const payload: NewFactorySchedulePayload = {
    factoryConfigId: automation.id,
    label: source?.label || automation.name,
    startDate: source?.startDate ?? Date.now(),
    timesToRun,
    repeatInterval,
    daysOfWeek: repeatInterval === 'weekly' ? daysOfWeek : undefined,
    dayOfMonth: repeatInterval === 'monthly' ? when.dayOfMonth ?? source?.dayOfMonth ?? 1 : undefined,
    status: source?.status === 'running' ? 'running' : 'enabled',
    // New records seed the scheduled runs' starting data from the current "What it works with"
    // inputs; existing records keep whatever payload was configured (e.g. in Advanced).
    initialCarryPayload: source ? source.initialCarryPayload : carryPayloadFromInputs(automation.inputs)
  }
  return source ? { ...source, ...payload } : payload
}

// Write direction — fold simple-lens workflow edits back onto the real WorkflowConfig. The prompt
// is only overlaid when the goal actually changed: an untouched `job` ships source.mainPrompt
// byte-for-byte (the Advanced editor's photocopy behavior), so field-only saves (name, status, lead)
// cannot alter helper tokens. An edited goal is sent with denormalized owned-agent names
// (helper-chip edits are folded into `job` by the detail view before this runs) and the backend
// normalizes names to refs, exactly as it does for Advanced saves. Fields the simple UI doesn't
// expose (delimiter, failureMessage/Instruction, …) pass through from the source so nothing is
// lost.
export function workflowConfigFromAutomation(
  automation: Automation,
  source: WorkflowConfig,
  agents: AgentCatalog
): WorkflowConfig {
  const lead = (automation.lead || '').trim()
  const leadRef = lead ? findAgentItemBySelector(lead, agents)?.ref : undefined
  // "Unchanged" means `job` equals what the read adapter would derive from this source right now —
  // the derivation must match automationFromWorkflow's exactly (same delimiter, ownedOnly), or every
  // save would look edited and silently lose the passthrough. A catalog change between read and save
  // (e.g. an agent rename) can still make an untouched job look edited; that fails toward the old
  // send-the-derived-text behavior, never toward dropping an edit.
  const derivedJob = denormalizeHelperAgentRefs(source.mainPrompt, source.delimiter, agents, { ownedOnly: true })
  return {
    ...source,
    name: automation.name,
    mainPrompt: automation.job === derivedJob ? source.mainPrompt : automation.job,
    mainAgentRef: leadRef ?? source.mainAgentRef,
    dataPayload: automation.dataPayload ?? source.dataPayload,
    concurrency: automation.concurrency ?? source.concurrency
  }
}

// Maps a simple-lens step transition back to the engine's shape.
function stepTransition(step: AutoStep): FactoryConfig['steps'][number]['transition'] {
  if (step.then === 'stop') return { kind: 'stop' }
  if (step.then === 'loop') return { kind: 'loop_to_index', targetIndex: step.loopTo ?? 0 }
  return { kind: 'next' }
}

// Mirrors FactoryEditor.buildExamplePayloadSchema — the starter payloadSchema Advanced seeds when
// a step becomes workflow-kind. Duplicated (not imported) so the Advanced editor stays untouched.
export function exampleWorkflowPayloadSchema(): Record<string, unknown> {
  return {
    type: 'object',
    properties: {
      input: {
        type: 'string',
        description: 'Example input passed into the workflow step'
      }
    },
    required: ['input'],
    additionalProperties: false
  }
}

// Write direction — fold simple-lens factory edits back onto the real FactoryConfig. Edited steps are
// matched to source steps by stepId (so promptOverride, workflowRef extras and invocation limits
// survive renames/reorders); steps added in the simple UI get the Advanced editor's new-step defaults.
// Agent titles resolve to canonical refs via the catalog; an unresolvable title keeps the source ref.
// Kind changes mirror FactoryEditor.onStepKindChanged: the other kind's ref is dropped and a fresh
// ref is seeded (workflow refs get the example payload schema and no repair attempts).
export function factoryConfigFromAutomation(
  automation: Automation,
  source: FactoryConfig,
  agents: AgentCatalog
): FactoryConfig {
  type FactoryStep = FactoryConfig['steps'][number]
  const sourceById = new Map((source.steps || []).map((s) => [s.stepId, s]))
  const steps = (automation.steps || []).map((step, index) => {
    const src = step.stepId ? sourceById.get(step.stepId) : undefined
    const kind = step.kind ?? src?.kind ?? 'agent'
    const agentRef = findAgentItemBySelector((step.agent || '').trim(), agents)?.ref ?? src?.agentRef?.agentRef ?? ''
    if (kind === 'workflow') {
      // The picker resolves name→id at selection time, so workflowId is set for user-edited steps;
      // the source fallbacks keep untouched steps lossless. New/kind-switched steps get the
      // Advanced kind-switch defaults (example schema, no repair attempts); an existing step with
      // no schema keeps none (schema-less steps only require a JSON object).
      const srcRef = src?.kind === 'workflow' ? src.workflowRef : undefined
      const workflowRef: NonNullable<FactoryStep['workflowRef']> = {
        ...(srcRef ?? {}),
        workflowConfigId: step.workflowId ?? srcRef?.workflowConfigId ?? '',
        maxRepairAttempts: step.maxRepairAttempts ?? srcRef?.maxRepairAttempts ?? 0
      }
      const schema = step.payloadSchema ?? srcRef?.payloadSchema ?? (srcRef ? undefined : exampleWorkflowPayloadSchema())
      if (schema) workflowRef.payloadSchema = schema
      else delete workflowRef.payloadSchema
      // Fixer: '' means cleared; a resolvable name becomes a canonical ref (superseding any legacy
      // bare id); an unresolvable name keeps the source's fixer fields (already spread above).
      const fixerName = (step.fixerAgent || '').trim()
      const fixerRef = fixerName ? findAgentItemBySelector(fixerName, agents)?.ref : undefined
      if (!fixerName) {
        delete workflowRef.fixerAgentRef
        delete workflowRef.fixerAgentId
      } else if (fixerRef) {
        workflowRef.fixerAgentRef = fixerRef
        delete workflowRef.fixerAgentId
      }
      const next: FactoryStep = {
        limitStepInvocations: false,
        maxStepInvocations: 1,
        ...src,
        stepId: src?.stepId ?? newId(),
        index,
        name: step.name,
        kind: 'workflow' as const,
        workflowRef,
        transition: stepTransition(step)
      }
      delete next.agentRef
      return next
    }
    const next: FactoryStep = {
      limitStepInvocations: false,
      maxStepInvocations: 1,
      ...src,
      stepId: src?.stepId ?? newId(),
      index,
      name: step.name,
      kind: 'agent' as const,
      agentRef: src?.kind === 'agent' ? { ...(src.agentRef ?? {}), agentRef } : { agentRef },
      transition: stepTransition(step)
    }
    delete next.workflowRef
    return next
  })
  return {
    ...source,
    name: automation.name,
    description: automation.job,
    continuous: automation.when.mode === 'continuous',
    steps
  }
}

export function automationsFromStores(args: {
  workflows: WorkflowConfig[]
  factories: FactoryConfig[]
  scheduledRuns: ScheduledRun[]
  agents?: AgentCatalog
  // Lookup for a factory's schedule records (store getter factorySchedulesByFactory).
  factorySchedules?: (factoryId: string) => FactoryScheduleRecord[]
}): Automation[] {
  const agents = args.agents ?? []
  return [
    ...args.workflows.map((w) => automationFromWorkflow(w, agents)),
    ...args.factories.map((f) => automationFromFactory(f, agents, args.workflows, args.factorySchedules?.(f.id) ?? [])),
    ...args.scheduledRuns.map(automationFromScheduledRun)
  ]
}
